import { useEffect } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { getMultisignWalletByAddress, getMultisignWalletById, getMyMultisignClaimById } from 'api';
import { useCurrentSafe, useSafes, useSafeSettings, useViewMode } from 'store';
import { IMultiSignWallet, StxChain } from 'types';
import { getStorageKeysForNetwork } from 'utils/LocalstorageUtils';

import { useSidebarState } from './sidebarState';
import useAddress from './use-address';
import useNetwork from './use-network';
import { isActivatedPro } from './useIsActivatedPro';

export const useLoadCurrentSafe = () => {
  const { safeAddress, safeId } = useParams<{ safeAddress?: string; safeId?: string }>();
  const { currentSafeId, setCurrentSafeId, setCurrentParentSafeId } = useSidebarState();
  const history = useHistory();
  const { safeSettings, setSafeSettings } = useSafeSettings();
  const { currentSafe, setCurrentSafe } = useCurrentSafe();
  const walletAccount = useAddress();
  const [network] = useNetwork();
  const { setIsReadOnly, setIsDeactivatedSafe } = useViewMode();
  const { pathname } = useLocation();

  const handleLoadingFailed = (err?: Error) => {
    const storageKeys = getStorageKeysForNetwork();
    // using value from useAuthorized here leads to premature redirect
    // TODO either change useAuthorized hook to get value from local store on init or somehow wait for initialization
    const isAuthorized = localStorage.getItem(
      storageKeys[network === 'mainnet' ? 'mainnetAddress' : 'testnetAddress']
    );
    console.error('Error loading wallet', err);
    history.push(isAuthorized ? '/' : '/');
  };

  const isPendingSafe = pathname.includes('/invite/');

  const { safes } = useSafes();

  useEffect(() => {
    if (!safeId && !safeAddress) return;

    // if already loaded or a pending safe
    if (currentSafe && currentSafe._id === currentSafeId) return;
    const net: StxChain =
      network === 'testnet' ? StxChain.STX_TESTNET_CHAIN : StxChain.STX_MAINNET_CHAIN;
    (safeId
      ? isPendingSafe
        ? getMyMultisignClaimById(safeId)
        : getMultisignWalletById(safeId)
      : getMultisignWalletByAddress(safeAddress || '', net)
    )
      .then((response) => {
        if (response.data) {
          setCurrentSafeId(response.data._id);
          setCurrentParentSafeId(response.data.parrentId || response.data._id);
          if (!isPendingSafe) {
            setIsReadOnly(
              walletAccount !== null &&
                !response.data.owners.some((owner) => owner.address === walletAccount)
            );
            setCurrentSafe(response.data as IMultiSignWallet);
            setSafeSettings({
              ...safeSettings,
              isPrivacyMode: Boolean((response.data as IMultiSignWallet).private),
            });
          }
        } else {
          handleLoadingFailed();
        }
      })
      .catch(handleLoadingFailed);
  }, [safeAddress, safeId, walletAccount]);

  useEffect(() => {
    if (!currentSafe) {
      return;
    }
    setIsReadOnly(!currentSafe.owners.some((owner) => owner.address === walletAccount));
    setIsDeactivatedSafe(!isActivatedPro(currentSafe, safes) && !!currentSafe.parrentId);
  }, [currentSafe, safes]);
};
